import * as Sentry from "@sentry/react";
import { Component, type ReactNode } from "react";

import { Button, EmptyState, Link } from "@marker/ui";

import { SUPPORT_MAILTO } from "../components/helpLinks";

interface ErrorBoundaryProps {
  readonly children: ReactNode;
  // Optional override so a nested boundary can render an inline fallback; the
  // root boundary uses the full-surface default below.
  readonly fallback?: ReactNode;
}

interface ErrorBoundaryState {
  readonly hasError: boolean;
}

/**
 * Root React error boundary for `marker-web` (ADR-0003, docs/09).
 *
 * A render-time exception anywhere below this boundary is caught here instead
 * of unmounting the whole SPA to a blank page, and is reported to Sentry via
 * the initialized client. Reporting is a no-op when Sentry is uninitialized —
 * air-gap never injects a DSN — and the `beforeSend` sanitizer in
 * `lib/sentry.ts` redacts the message + stack frame values before any event
 * leaves the browser, so no PII/credential rides out. The session stays an
 * HttpOnly cookie; this path never touches token storage.
 */
export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  override state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error): void {
    // Capture the full error (message + stack) to Sentry. `beforeSend` scrubs
    // the value + frames before egress; no-op if Sentry was never initialized.
    Sentry.captureException(error);
  }

  private readonly handleReload = (): void => {
    window.location.reload();
  };

  override render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }
    if (this.props.fallback !== undefined) {
      return this.props.fallback;
    }
    return (
      <div className="marker-error-boundary" role="alert">
        <EmptyState
          title="Something went wrong"
          description={
            <>
              Reload the page. If this keeps happening,{" "}
              <Link href={SUPPORT_MAILTO}>contact support</Link>.
            </>
          }
          action={<Button onClick={this.handleReload}>Reload</Button>}
        />
      </div>
    );
  }
}
